<template>
  <div
    class="app-hub-sidebar"
    :class="{
      'app-hub-sidebar--rail': iconMode,
      'app-hub-sidebar--expanded': !iconMode && !mobile,
      'app-hub-sidebar--mobile': mobile
    }"
  >
    <!-- Liste: scrollt selbst; „Einstellungen“ steht unten (margin-top: auto) -->
    <nav ref="scroll" class="app-hub-sidebar__content" aria-label="Hauptnavigation">
      <!-- Aufgeklappt und in der Schublade: Hauptpunkt ohne Label, Gruppen als klappbare Abschnitte -->
      <template v-if="!iconMode">
        <div
          v-for="group in blocks"
          :key="group.id"
          class="app-hub-sidebar__group"
          :class="{ 'app-hub-sidebar__group--foot': group === footGroup, 'app-hub-sidebar__group--closed': !!group.children && !isOpen(group.id) }"
        >
          <ul v-if="!group.children" class="app-hub-sidebar__menu">
            <li>
              <router-link
                :to="group.path"
                class="app-hub-sidebar__item"
                :aria-current="isActivePath(group.path) ? 'page' : null"
                @click.native="onNavigate($event, group.path)"
              >
                <app-icon :name="group.icon" :size="16" class="app-hub-sidebar__icon" />
                <span class="app-hub-sidebar__text">{{ group.title }}</span>
              </router-link>
            </li>
          </ul>
          <template v-else>
            <button
              :id="labelId(group.id)"
              type="button"
              class="app-hub-sidebar__label"
              :class="{ 'app-hub-sidebar__label--here': isHereClosed(group) }"
              :aria-expanded="isOpen(group.id) ? 'true' : 'false'"
              :aria-controls="menuId(group.id)"
              :aria-current="isHereClosed(group) ? 'true' : null"
              @click="toggle(group.id)"
            >
              <span class="app-hub-sidebar__label-text">{{ group.title }}</span>
              <app-icon :name="isOpen(group.id) ? 'chevD' : 'chevR'" :size="14" class="app-hub-sidebar__chev" />
            </button>
            <ul
              v-show="isOpen(group.id)"
              :id="menuId(group.id)"
              class="app-hub-sidebar__menu"
              :aria-labelledby="labelId(group.id)"
            >
              <li v-for="entry in group.children" :key="entry.path">
                <router-link
                  :to="entry.path"
                  class="app-hub-sidebar__item"
                  :aria-current="isActivePath(entry.path) ? 'page' : null"
                  @click.native="onNavigate($event, entry.path)"
                >
                  <app-icon :name="entry.icon" :size="16" class="app-hub-sidebar__icon" />
                  <span class="app-hub-sidebar__text">{{ entry.title }}</span>
                </router-link>
              </li>
            </ul>
          </template>
        </div>
      </template>

      <!-- Eingeklappt (nur Desktop): Icon-Knöpfe; Übersicht ist ein Link, jede Gruppe öffnet ihre Seiten im Flyout -->
      <template v-else>
        <div
          v-for="block in railBlocks"
          :key="block.key"
          class="app-hub-sidebar__group"
          :class="{ 'app-hub-sidebar__group--foot': block.key === 'rail-foot' }"
        >
          <ul class="app-hub-sidebar__menu">
            <li v-for="group in block.groups" :key="group.id" class="app-hub-sidebar__entry">
              <template v-if="!group.children">
                <router-link
                  :id="linkId(group.id)"
                  :to="group.path"
                  class="app-hub-sidebar__item app-hub-sidebar__item--icon"
                  :aria-label="group.title"
                  :aria-current="isActivePath(group.path) ? 'page' : null"
                  @click.native="onNavigate($event, group.path)"
                >
                  <app-icon :name="group.icon" :size="16" class="app-hub-sidebar__icon" />
                </router-link>
                <v-tooltip
                  v-if="tipsOn"
                  :activator="'#' + linkId(group.id)"
                  right
                  open-delay="400"
                  content-class="app-tooltip"
                >
                  <span>{{ group.title }}</span>
                </v-tooltip>
              </template>
              <app-nav-flyout v-else :group="group" :active-path="activePath" :touch="touch" tooltip>
                <template v-slot:activator="{ on, attrs }">
                  <button
                    type="button"
                    class="app-hub-sidebar__item app-hub-sidebar__item--icon"
                    v-bind="attrs"
                    :aria-label="group.title"
                    :aria-current="group.id === activeGroupId ? 'true' : null"
                    v-on="on"
                  >
                    <app-icon :name="group.icon" :size="16" class="app-hub-sidebar__icon" />
                  </button>
                </template>
              </app-nav-flyout>
            </li>
          </ul>
        </div>
      </template>
    </nav>

    <!-- Fußzeile wie im Mobility Hub: nur der Modus-Knopf; in der Schublade ausgeblendet -->
    <div v-if="!mobile" class="app-hub-sidebar__foot">
      <button
        type="button"
        class="app-hub-sidebar__mode"
        :aria-label="modeLabel"
        :title="modeTitle"
        :aria-keyshortcuts="modeKeys"
        @click="toggleCollapse"
      >
        <app-icon :name="iconMode ? 'panelOpen' : 'panelClose'" :size="16" class="app-hub-sidebar__mode-icon" />
      </button>
    </div>
  </div>
</template>

<script>
// Navigation der Shell „Mobility Hub“ (Aufbau, Maße und Verhalten aus dem Mobility Hub, Analyse „sidebar“,
// „sidebarModes“ und „mobile“; Farben aus tokens.css). Die Komponente ist nur die innere Spalte: Position (fixed unter
// der Kopfleiste), Breite (256, Icon-Leiste 48 bzw. Touch 60, Schublade 288) und die Kante rechts setzt die Shell
// (MainLayout); die Spalte füllt die Höhe, --surface (hell weiß), Liste oben und Fußzeile unten.
// Aufgeklappt (auch in der Schublade unter 768 px): oben „Übersicht“ als Eintrag ohne Label, darunter jede Gruppe als
// Abschnitt wie im Mobility Hub – Label 32 (Touch 44) in Großbuchstaben 12/16 500 als Knopf zum Auf- und Zuklappen
// (Chevron rechts), Einträge 32 (Touch 44) mit Icon 16 (20) und Text 14/20; „Einstellungen“ steht ganz unten, durch
// eine Linie abgesetzt, wie ORGANISATION im Mobility Hub. Aktiver Eintrag: neutrale getönte Kachel (--tint), Text 600,
// Icon orange (Ort); ist sein Abschnitt zugeklappt, zeigt das Label den Ort.
// Eingeklappt (nur Desktop): Icon-Knöpfe 32 (Touch 44) auf der Achse der Icons; Übersicht ist ein Link, jede Gruppe
// öffnet ihre Seiten im Flyout (AppNavFlyout) – anders als im Mobility Hub gibt es keine toten Klicks und keine
// ausgeblendeten Gruppen. Tooltips rechts nur mit Maus und nur, solange kein Navigations-Popup offen ist.
// Fußzeile 40 (Touch 52) mit Linie oben: nur der Modus-Knopf (Seitenleiste ein-/ausklappen, Tastenkürzel Strg/⌘+B
// wertet die Shell aus); aufgeklappt rechts, eingeklappt auf der Achse der Icon-Knöpfe.
// Offene Abschnitte: die Gruppe der aktiven Seite öffnet bei jedem Routenwechsel automatisch (nicht gemerkt), weitere
// öffnet und schließt der Nutzer per Klick (gemerkt in shellState, gemeinsam mit der klassischen Seitenleiste). Die
// Liste holt nach jedem Routenwechsel den aktiven Punkt und nach dem Aufklappen den Abschnitt in den sichtbaren Teil.
// Props: groups (visibleNavigation), activePath (normalisiert), activeGroupId, collapsed (Icon-Leiste), mobile
// (Schublade: immer aufgeklappt, ohne Fußzeile und Tooltips).
// Ereignisse: navigate(path) nach einem Klick auf einen Link der Spalte (nicht bei Strg/⌘-Klick in einen neuen Tab;
// die Shell schließt damit die Schublade), toggle-collapse(collapsed) vom Modus-Knopf mit dem gewünschten Zustand.
import AppNavFlyout from '../shell/AppNavFlyout.vue';
import { normalizePath } from '../../shell/navigation';

// Gruppe ganz unten, durch eine Linie abgesetzt (wie ORGANISATION im Mobility Hub)
const FOOT_GROUP_ID = 'einstellungen';

// Tastenkürzel im Titel des Modus-Knopfs: ⌘ auf Apple-Geräten (auch iPad mit Tastatur), sonst Strg
const APPLE = typeof navigator !== 'undefined' && /Mac|iPhone|iPad|iPod/.test(navigator.platform || '');

// darstellbar: Gruppe mit Einträgen oder Hauptpunkt mit Pfad
function renderable(group) {
  return group.children ? group.children.length > 0 : !!group.path;
}

export default {
  name: 'AppHubSidebar',
  components: { AppNavFlyout },
  props: {
    groups: { type: Array, required: true },
    activePath: { type: String, default: '' },
    activeGroupId: { type: String, default: null },
    // Icon-Leiste (nur Desktop; in der Schublade wirkungslos)
    collapsed: { type: Boolean, default: false },
    // in der Schublade unter 768 px: immer aufgeklappt, ohne Fußzeile und Tooltips
    mobile: { type: Boolean, default: false }
  },
  data() {
    return {
      shortcut: APPLE ? '⌘B' : 'Strg+B',
      modeKeys: APPLE ? 'Meta+B' : 'Control+B'
    };
  },
  computed: {
    touch() {
      return !!(this.$dichte && this.$dichte.touch);
    },
    iconMode() {
      return this.collapsed && !this.mobile;
    },
    shown() {
      return this.groups.filter(renderable);
    },
    footGroup() {
      return this.shown.find(group => group.id === FOOT_GROUP_ID) || null;
    },
    mainGroups() {
      return this.shown.filter(group => group.id !== FOOT_GROUP_ID);
    },
    // aufgeklappt: ein Abschnitt je Gruppe, Einstellungen zuletzt (unten)
    blocks() {
      return this.footGroup ? this.mainGroups.concat([this.footGroup]) : this.mainGroups;
    },
    // eingeklappt: alle Knöpfe in einer Liste (Raster 36), Einstellungen getrennt unten
    railBlocks() {
      const blocks = [];
      if (this.mainGroups.length) {
        blocks.push({ key: 'rail-main', groups: this.mainGroups });
      }
      if (this.footGroup) {
        blocks.push({ key: 'rail-foot', groups: [this.footGroup] });
      }
      return blocks;
    },
    tipsOn() {
      return !this.mobile && !this.touch && !this.$shell.popupOffen;
    },
    // Gruppe der aktiven Seite, sofern sie als Abschnitt sichtbar ist
    autoGroupId() {
      const id = this.activeGroupId;
      return id && this.shown.some(group => group.id === id && group.children) ? id : null;
    },
    modeLabel() {
      return this.iconMode ? 'Seitenleiste ausklappen' : 'Seitenleiste einklappen';
    },
    modeTitle() {
      return this.modeLabel + ' (' + this.shortcut + ')';
    }
  },
  watch: {
    activePath: {
      immediate: true,
      handler() {
        this.$shell.setAutoGroup(this.autoGroupId);
        this.$nextTick(this.revealActive);
      }
    },
    // Navigation kommt später (Profil und Rechte geladen): Gruppe der aktiven Seite dann nachträglich öffnen
    autoGroupId(id) {
      this.$shell.setAutoGroup(id);
      this.$nextTick(this.revealActive);
    },
    // anderer Modus, andere Elemente: aktiven Punkt wieder in den sichtbaren Teil holen
    iconMode() {
      this.$nextTick(this.revealActive);
    }
  },
  methods: {
    isActivePath(path) {
      return normalizePath(path) === this.activePath;
    },
    isOpen(id) {
      // shell.openGroups und shell.autoGroup sind reaktiv; isGroupOpen liest beide
      return this.$shell.isGroupOpen(id);
    },
    // aktive Gruppe zugeklappt: das Label zeigt den Ort
    isHereClosed(group) {
      return group.id === this.activeGroupId && !this.isOpen(group.id);
    },
    toggle(id) {
      const opening = !this.isOpen(id);
      this.$shell.toggleGroup(id);
      if (opening) {
        this.$nextTick(() => this.revealSection(id));
      }
    },
    labelId(id) {
      return 'app-hub-sidebar-' + this._uid + '-' + id + '-label';
    },
    menuId(id) {
      return 'app-hub-sidebar-' + this._uid + '-' + id;
    },
    // Link der Icon-Leiste: Ziel des Tooltips (activator als Selektor; router-link reicht keine Maus-Ereignisse durch)
    linkId(id) {
      return 'app-hub-sidebar-' + this._uid + '-' + id + '-link';
    },
    onNavigate(event, path) {
      // wie router-link: Klick mit Strg/⌘/Umschalt/Alt oder anderer Maustaste navigiert hier nicht (neuer Tab)
      if (event && (event.metaKey || event.altKey || event.ctrlKey || event.shiftKey ||
        (event.button !== undefined && event.button !== 0))) {
        return;
      }
      this.$emit('navigate', path);
    },
    // Der Knopf bleibt beim Umschalten dasselbe Element: der Fokus bleibt auf ihm
    toggleCollapse() {
      this.$emit('toggle-collapse', !this.collapsed);
    },
    // aktiven Punkt (zugeklappt: Label bzw. Gruppenknopf) in den sichtbaren Teil der Liste holen; wie
    // scrollIntoView({ block: 'nearest' }), aber nur in der eigenen Liste (Shell und Fenster bleiben stehen), mit 8 Luft
    // für Scrollschatten und Fokusring
    revealActive() {
      const list = this.$refs.scroll;
      const here = list ? list.querySelector('[aria-current="page"], [aria-current="true"]') : null;
      if (!here || !here.getClientRects().length) {
        return;
      }
      const view = list.getBoundingClientRect();
      const box = here.getBoundingClientRect();
      if (box.top < view.top + 8) {
        list.scrollTop -= view.top + 8 - box.top;
      } else if (box.bottom > view.bottom - 8) {
        list.scrollTop += box.bottom - view.bottom + 8;
      }
    },
    // Abschnitt aufgeklappt, sein Ende liegt unter dem sichtbaren Teil (etwa Einstellungen ganz unten): so weit
    // scrollen, dass die Einträge sichtbar werden, höchstens bis das Label oben ankommt
    revealSection(id) {
      const list = this.$refs.scroll;
      const label = document.getElementById(this.labelId(id));
      const menu = document.getElementById(this.menuId(id));
      if (!list || !label || !menu || !menu.getClientRects().length) {
        return;
      }
      const view = list.getBoundingClientRect();
      const below = menu.getBoundingClientRect().bottom + 8 - view.bottom;
      const room = label.getBoundingClientRect().top - view.top - 8;
      if (below > 0 && room > 0) {
        list.scrollTop += Math.min(below, room);
      }
    }
  }
};
</script>

<style scoped>
/* Spalte: füllt die Fläche, die die Shell unter der Kopfleiste fixiert (Block oder Flex-Spalte). Maße nach Dichte:
   Einträge, Labels und Knöpfe 32 (Touch 44); Icons 16 (Touch 20) stehen in einem Quadrat der Eintragshöhe (Polster 8
   bzw. 12) – damit liegen sie aufgeklappt und eingeklappt auf derselben Achse (x 16 bzw. 20) und springen beim
   Umschalten nicht, während die Shell die Breite animiert. */
.app-hub-sidebar {
  --hsb-h: var(--h-ctl);
  --hsb-px: 8px;
  --hsb-icon: 16px;
  flex: 1 1 auto;
  width: 100%;
  height: 100%;
  min-height: 0;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  background: var(--surface);
  color: var(--ink);
  font-family: var(--font);
  font-size: 14px;
  line-height: 20px;
}
html.dichte-touch .app-hub-sidebar {
  --hsb-px: 12px;
  --hsb-icon: 20px;
}
/* Aufgeklappt auf dem Desktop: Layout sofort in der Endbreite (Breite der Navigation aus hub.css ohne die Kante der
   Shell); die Shell animiert ihre Breite mit overflow: hidden und deckt die Spalte beim Ausklappen nur auf – die Texte
   erscheinen wie hinter einem Vorhang statt mit wanderndem „…“ (wie die feste Breite der klassischen Seitenleiste).
   Ohne die Variable gilt die Breite der Shell. */
.app-hub-sidebar--expanded {
  min-width: calc(var(--hub-nav-w, 100%) - 1px);
}

/* Liste (MH SidebarContent): oben 8, Abschnitte mit 8 Abstand; scrollt selbst, Scrollschatten oben und unten nur,
   wenn dort Inhalt verdeckt ist (Abdeckung wandert mit dem Inhalt, Schatten bleibt am Rand; wie die klassische
   Seitenleiste, hier auf --surface: die Abdeckung ist deckend in der Spaltenfarbe, im Dunkeln also dunkel) */
.app-hub-sidebar__content {
  flex: 1 1 auto;
  min-height: 0;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin: 0;
  padding: 8px 0 0;
  overflow-x: hidden;
  overflow-y: auto;
  overscroll-behavior: contain;
  scrollbar-width: thin;
  background:
    linear-gradient(var(--surface) 30%, transparent) top / 100% 24px no-repeat local,
    linear-gradient(transparent, var(--surface) 70%) bottom / 100% 24px no-repeat local,
    radial-gradient(farthest-side at 50% 0, var(--sh-scroll), transparent) top / 100% 8px no-repeat scroll,
    radial-gradient(farthest-side at 50% 100%, var(--sh-scroll), transparent) bottom / 100% 8px no-repeat scroll;
  background-color: var(--surface);
}

/* Abschnitt (MH SidebarGroup): Polster 8; Label und Liste ohne Abstand. Erster Punkt damit bei y 16, Label 16–48,
   erster Eintrag ab 48; Raster 36 (32 + 4), von Abschnitt zu Abschnitt 24 Luft */
.app-hub-sidebar__group {
  flex: none;
  min-width: 0;
  display: flex;
  flex-direction: column;
  padding: 8px;
}
/* Einstellungen wie ORGANISATION: ans Ende geschoben, Linie oben, darunter 8 Abstand und das Polster des Abschnitts */
.app-hub-sidebar__group--foot {
  margin-top: auto;
  padding-top: 16px;
  border-top: 1px solid var(--line);
}
/* zugeklappte Abschnitte rücken zusammen (im Mobility Hub sind Gruppen nie zugeklappt): nur der Titel im Raster der
   Einträge, statt 56 px Abstand nur 36 */
.app-hub-sidebar__group--closed:not(.app-hub-sidebar__group--foot) {
  padding-top: 0;
  padding-bottom: 0;
}

.app-hub-sidebar__menu {
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin: 0;
  padding: 0;
  list-style: none;
}
.app-hub-sidebar__entry {
  display: flex;
}

/* Gruppenlabel (shadcn „collapsible group“): 32 (Touch 44), Text bündig mit den Icons, 12/16 500 in Großbuchstaben
   (nur per CSS: Screenreader lesen den Titel normal), Chevron rechts */
.app-hub-sidebar__label {
  width: 100%;
  height: var(--hsb-h);
  box-sizing: border-box;
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0;
  padding: 0 var(--hsb-px);
  border: 0;
  border-radius: var(--r-ctl);
  background: transparent;
  color: var(--ink-3);
  font-family: var(--font);
  font-size: 12px;
  line-height: 16px;
  font-weight: 500;
  letter-spacing: normal;
  text-transform: uppercase;
  text-align: left;
  white-space: nowrap;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}
.app-hub-sidebar__label-text {
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
}
.app-hub-sidebar__chev {
  flex: none;
  width: var(--chev);
  height: var(--chev);
  color: var(--ink-3);
}

/* Eintrag (MH SidebarMenuButton): 32 (Touch 44), Polster 0 8 (Touch 12), Abstand 8, 14/20 400; Text gekürzt */
.app-hub-sidebar__item {
  width: 100%;
  height: var(--hsb-h);
  box-sizing: border-box;
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0;
  padding: 0 var(--hsb-px);
  border: 0;
  border-radius: var(--r-ctl);
  background: transparent;
  color: var(--ink-2);
  font-family: var(--font);
  font-size: 14px;
  line-height: 20px;
  font-weight: 400;
  letter-spacing: normal;
  text-align: left;
  text-decoration: none;
  white-space: nowrap;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}
.app-hub-sidebar__icon {
  flex: none;
  width: var(--hsb-icon);
  height: var(--hsb-icon);
  color: var(--ink-3);
}
.app-hub-sidebar__text {
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Icon-Leiste: quadratische Knöpfe 32 (Touch 44) ab x 8 – in der Leiste 48 (Touch 60) mittig – auf fester Position,
   damit beim Einklappen nichts wandert; ohne rechtes Polster, damit der Knopf in die schmale Spalte passt. Die
   Scrollleiste ist hier ausgeblendet (Scrollen und Scrollschatten bleiben). */
.app-hub-sidebar__item--icon {
  flex: none;
  width: var(--hsb-h);
  padding: 0;
  justify-content: center;
}
.app-hub-sidebar--rail .app-hub-sidebar__group {
  padding-right: 0;
}
.app-hub-sidebar--rail .app-hub-sidebar__content {
  scrollbar-width: none;
}
.app-hub-sidebar--rail .app-hub-sidebar__content::-webkit-scrollbar {
  display: none;
}

/* Fußzeile (MH SidebarFooter): 40 (Touch 52) einschließlich Linie oben; Knopf 32 (Touch 44) ganzzahlig darin
   (oben 3 + Linie 1, unten 4); aufgeklappt rechts (x 219), eingeklappt auf der Achse der Icon-Knöpfe (x 8) */
.app-hub-sidebar__foot {
  flex: none;
  height: calc(var(--h-ctl) + 8px);
  box-sizing: border-box;
  display: flex;
  align-items: center;
  padding: 3px 4px 4px;
  border-top: 1px solid var(--line);
}
.app-hub-sidebar__mode {
  flex: none;
  width: var(--hsb-h);
  height: var(--hsb-h);
  box-sizing: border-box;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin: 0 0 0 auto;
  padding: 0;
  border: 0;
  border-radius: var(--r-ctl);
  background: transparent;
  color: var(--ink-3);
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}
.app-hub-sidebar--rail .app-hub-sidebar__mode {
  margin-left: 4px;
}
/* Icon in der Farbe des Knopfs (--ink-3, bei Hover --ink) */
.app-hub-sidebar__mode-icon {
  flex: none;
  width: var(--hsb-icon);
  height: var(--hsb-icon);
}

/* Zustände: Hover nur Maus (Flyout offen wie Hover) → aktiv → gedrückt. Aktiv ist der Ort, kein Knopf: neutrale
   getönte Kachel (--tint) ohne Kante und Schatten, Text 600, Icon orange; zugeklappter Abschnitt mit der aktiven Seite
   ebenso. */
:where(html:not(.dichte-touch)) .app-hub-sidebar__item:hover,
:where(html:not(.dichte-touch)) .app-hub-sidebar__label:hover,
.app-hub-sidebar__item[aria-expanded="true"] {
  background: var(--hov);
}
:where(html:not(.dichte-touch)) .app-hub-sidebar__mode:hover {
  background: var(--hov);
  color: var(--ink);
}
.app-hub-sidebar__item[aria-current],
.app-hub-sidebar__label.app-hub-sidebar__label--here {
  background: var(--tint);
  color: var(--ink);
  font-weight: 600;
}
.app-hub-sidebar__item[aria-current] .app-hub-sidebar__icon {
  color: var(--acc);
}
.app-hub-sidebar__item:active,
.app-hub-sidebar__label:active,
.app-hub-sidebar__mode:active {
  background: var(--press);
}

/* Fokus: 2 px Navy mit 2 Abstand, nur bei Tastaturbedienung (die Abschnitte lassen rundum 8 Platz dafür) */
.app-hub-sidebar__item:focus,
.app-hub-sidebar__label:focus,
.app-hub-sidebar__mode:focus {
  outline: none;
}
.app-hub-sidebar__item:focus-visible,
.app-hub-sidebar__label:focus-visible,
.app-hub-sidebar__mode:focus-visible {
  outline: 2px solid var(--link);
  outline-offset: 2px;
}
</style>
